import { Field } from '@catering/ui';
import { type JSX, useEffect, useRef, useState } from 'react';

import {
  CONTRACT_STATUSES,
  CONTRACT_STATUS_LABELS,
  type ContractStatus,
} from '../../../contracts/api';
import { OperatingUnitSelect } from '../../../components/operating-unit-select';
import type { CustomerListItem } from '../../../customers/api';

const SEARCH_DEBOUNCE_MS = 300;

export interface ContractSearchBarProps {
  query: string;
  customerId: string | undefined;
  status: ContractStatus | undefined;
  operatingUnitId: string;
  customers: CustomerListItem[];
  onQueryChange: (query: string) => void;
  onCustomerChange: (customerId: string | undefined) => void;
  onStatusChange: (status: ContractStatus | undefined) => void;
  /** `''` means every point of sale. */
  onOperatingUnitChange: (operatingUnitId: string) => void;
}

/**
 * The text field debounces locally; customer/status are `<select>`s that
 * commit immediately, same convention as `CustomerSearchBar`/`MenuSearchBar`
 * (`design.md` §7: no shared `Select` component yet).
 */
export function ContractSearchBar({
  query,
  customerId,
  status,
  operatingUnitId,
  customers,
  onQueryChange,
  onCustomerChange,
  onStatusChange,
  onOperatingUnitChange,
}: ContractSearchBarProps): JSX.Element {
  const [draft, setDraft] = useState(query);
  const [syncedQuery, setSyncedQuery] = useState(query);

  if (query !== syncedQuery) {
    setSyncedQuery(query);
    setDraft(query);
  }

  const onQueryChangeRef = useRef(onQueryChange);
  useEffect(() => {
    onQueryChangeRef.current = onQueryChange;
  });

  useEffect(() => {
    const trimmed = draft.trim();

    if (trimmed === query) {
      return;
    }

    const timeout = setTimeout(() => onQueryChangeRef.current(trimmed), SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  }, [draft, query]);

  return (
    <div className="flex flex-wrap items-end gap-4">
      <div className="min-w-64 flex-1">
        <Field
          label="Buscar"
          name="q"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Código de contrato"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="contract-customer-filter" className="text-sm font-medium text-slate-900">
          Cliente
        </label>
        <select
          id="contract-customer-filter"
          value={customerId ?? ''}
          onChange={(event) =>
            onCustomerChange(event.target.value === '' ? undefined : event.target.value)
          }
          className="h-11 rounded-md border border-slate-300 px-3 text-sm"
        >
          <option value="">Todos los clientes</option>
          {customers.map((customer) => (
            <option key={customer.id} value={customer.id}>
              {customer.legalName}
            </option>
          ))}
        </select>
      </div>

      <OperatingUnitSelect
        id="contract-operating-unit-filter"
        label="Punto de venta"
        allLabel="Todos los puntos de venta"
        value={operatingUnitId}
        onChange={onOperatingUnitChange}
      />

      <div className="flex flex-col gap-1.5">
        <label htmlFor="contract-status-filter" className="text-sm font-medium text-slate-900">
          Estado
        </label>
        <select
          id="contract-status-filter"
          value={status ?? ''}
          onChange={(event) =>
            onStatusChange(
              event.target.value === '' ? undefined : (event.target.value as ContractStatus),
            )
          }
          className="h-11 rounded-md border border-slate-300 px-3 text-sm"
        >
          <option value="">Todos</option>
          {CONTRACT_STATUSES.map((s) => (
            <option key={s} value={s}>
              {CONTRACT_STATUS_LABELS[s]}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
